import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { ChartData } from '../../../helpers/chart.data';
import { ClassTransformerHelper } from '../../../helpers/class.transformer.helper';
import { FilterHelper } from '../../../helpers/filter.helper';
import { MetaHelper } from '../../../helpers/meta.helper';
import { ChartSettings } from '../../../models/controls/chart.model';
import { AxisTextPosition } from '../../../models/enums/axistextposition.enum';
import { ElementProperty } from '../../../models/layoutbase.model';
import { RequestFilter } from '../../../models/rest/requestfilter';
import { Color } from '../../../models/style/color.model';
import { DataService } from '../../../services/data.service';
import { LayoutService } from '../../../services/layout.service';
import { SettingsService } from '../../../services/settings.service';
import { IBaseComponent } from '../base.component';
import { Comparer } from '../../../models/enums/comparer.enum';

@Component({
    selector: 'evi-charts',
    templateUrl: './charts.control.html',
    styleUrls: ['./charts.control.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChartsControl extends IBaseComponent {

    static Type: any = 'charts';
    static Default = {
        Caption: 'Charts',
        Type: 'charts',
        Layout: {
            vAxisTextPosition: AxisTextPosition.out,
            hAxisTextPosition: AxisTextPosition.out,
            _Editable: true
        }
    };
    static Meta = {
                Type: 'charts',
                Properties: [
                    { Name: 'DataSource', Type: 'any' },
                    { Name: 'ChartSettings', Type: 'any' },
                ],
                Events: [
                    { Name: 'SelectionChanged' },
                ]
            };
    private SourceValue: any[];
    get Source() {
        return this.SourceValue;
    }
    set Source(val) {
        this.FillSource(val);
    }

    ChartData: any;

    //#region DataSource
    DataSourceValue: any;
    @Input()
    get DataSource() {
        return this.DataSourceValue;
    }
    set DataSource(val) {
        this.DataSourceValue = val;
        this.RefreshInternal();
        this.DataSourceChange.emit(val);
        this.triggerEvent('DataSourceChanged', this.DataSourceValue);
    }
    @Output() DataSourceChange = new EventEmitter<any>();
    //#endregion
    //#region ChartSettings
    ChartSettingsValue: any;
    @Input()
    get ChartSettings() {
        if (this.ChartSettingsValue != null) {
            return this.ChartSettingsValue;
        }
        return this.LayoutElement.ChartSettings;
    }
    set ChartSettings(val) {
        this.ChartSettingsValue = val;
        this.SetChartData();
    }
    //#endregion

    CSSStyles: any;
    GeneralSettings = null;
    DataOptions = null;

    constructor(private dataService: DataService, cdRef: ChangeDetectorRef, @Inject(LayoutService.CONTAINER_DATA) public data: any) {
        super(cdRef, data);
        this.EventList.push('SelectionChanged');
        let settings = localStorage.getItem("GeneralSettings");
        if (settings) {
            this.GeneralSettings = JSON.parse(settings);
        }
        SettingsService.GeneralSettings.subscribe((settings) => {
            this.GeneralSettings = settings;
        });
        this.PropertyList.push(new ElementProperty('Source', 'list', '@@Source'));
        this.PropertyList.push(new ElementProperty('ChartSettings', 'object', '@@ChartSettings'));
    }

    ChartPropChanged(prop: any) {
        this.SetCSSClass();
        this.SetChartData();
    }

    ControlInitialized() {
        this.ExecuteRefresh();
    }

    SetCSSClass() {
        const head = document.getElementsByTagName('head')[0];
        if (this.CSSStyles) {
            this.CSSStyles.forEach((style: any) => {
                try {
                    head.removeChild(style);
                } catch {}
            })
        }
        this.CSSStyles = [];        
        if (this.LayoutElementValue && this.LayoutElementValue.Font && this.LayoutElementValue.Font.FontColor) {
            const colorText = Color.GetStyleText(this.LayoutElementValue.Font.FontColor);
            if (colorText) {
                let radialstyle = document.createElement('style');
                radialstyle.appendChild(document.createTextNode('.LE_' + this.LayoutElementValue.ID + ' > *, .text-stroke { fill: ' + colorText+'!important; }'));
                this.CSSStyles.push(radialstyle);
                head.appendChild(radialstyle);

                let angularstyle = document.createElement('style');
                angularstyle.appendChild(document.createTextNode('.LE_' + this.LayoutElementValue.ID + ' > *, .text-stroke { fill: ' + colorText+'!important; }'));
                this.CSSStyles.push(angularstyle);
                head.appendChild(angularstyle);
            } 
        }
        this.cdRef.markForCheck();
    }

    ExecuteRefresh() {
        this.FillSource(null);
        this.SetCSSClass();
        this.FillDataOptions();
    }

    private FillDataOptions() {
        MetaHelper.FindDataBindingProperties(this.Layout, this.LayoutElementValue).then((result: any) => {
            const options: any = {
                DataSourceElement: null,
                UseAdditionalFilter: false,
                QueryID: null,
                DateFields: []
            };
            if (result) {
                if (result.IsDataBinding) {
                    if (result.IsShared) {
                        options.DataSourceElement = this.LayoutElementValue;
                        options.UseAdditionalFilter = true;
                    }
                } else {
                    options.DataSourceElement = MetaHelper.FindValidParent(this.Layout, this.LayoutElementValue);
                }
                if (result.Table) {
                    options.QueryID = result.Table.SID;
                    if (result.Table.Fields) {
                        result.Table.Fields.forEach((x: any) => {
                            if (x.DataTyp === 'System.DateTime') {
                                options.DateFields.push(x.Name);
                            }
                        });
                    }
                }
            }
            this.DataOptions = options;
            this.RefreshInternal();
        });
    }

    private RefreshInternal() {
        if (this.DataOptions && this.DataOptions.DataSourceElement) {
            const filter = FilterHelper.PrepareFilter(this.DataOptions.DataSourceElement);
            if (this.Variables && this.Layout.Filter) {
                this.Layout.Filter.forEach((fil: any) => {
                    if (this.Variables[fil.Filter]) {
                        const item = new RequestFilter();
                        item.Name = fil.Name;
                        item.Operator = fil.Comparer;
                        item.Value = this.Variables[fil.Filter];
                        FilterHelper.AddSearchFilter(filter, item);
                    }
                });
            }
            if (this.DataOptions.UseAdditionalFilter) {
                const addFilter = new RequestFilter();
                addFilter.Name = '_Id';
                addFilter.Operator = Comparer.In;
                addFilter.Value = [];
                if (Array.isArray(this.DataSourceValue)) {
                    addFilter.Value.push(...this.DataSourceValue);
                }
                FilterHelper.AddSearchFilter(filter, addFilter);
            }
            this.dataService.SearchObjects('dynamicdata', this.DataOptions.QueryID, filter).subscribe((data) => {
                this.FillSource(data);
            });
        } else {
            this.FillSource(this.DataSourceValue);
        }
    }

    SelectionChanged(event: any) {
        this.triggerEvent('SelectionChanged', event);
    }

    private FillSource(source: any[]) {
        let data: any[];
        if (source && source.length > 0) {
            data = [];
            let dateFields = [];
            if (this.DataOptions && this.DataOptions.DateFields) {
                dateFields = this.DataOptions.DateFields;
            }
            source.forEach(x => {
                const dataVal = Object.assign({}, x);
                dateFields.forEach(y => {
                    const val = dataVal[y];
                    if (typeof val !== 'undefined' && val !== null && !(val instanceof Date)) {
                        dataVal[y] = new Date(val);
                    }
                });
                data.push(dataVal);
            });
        }
        this.SourceValue = data;
        this.SetChartData();
    }

    private SetChartData() {
        let chartData: any;
        const source = this.SourceValue;
        if (source && source.length > 0) {
            const settings = this.ChartSettings;
            if (settings) {
                const copy = ClassTransformerHelper.CopyObject(settings, ChartSettings);
                if (this.LayoutElementValue && this.LayoutElementValue.UsePaletteForSingleSeries &&
                    copy.Series && copy.Series.length == 1) {
                    ChartData.GetPalette(copy).then(x => {
                        if (x && x.length > 0) {
                            const styleKey = '_PalleteStyle_';
                            copy.Series[0]['StyleKey'] = styleKey;
                            source.forEach((row, i) => {
                                row[styleKey] = '' + (i % x.length);
                            });
                            const styles = {};
                            x.forEach((y, i) => {
                                styles['' + i] = y;
                            });
                            this.ChartData = {
                                Data: {
                                    Data: source,
                                    Styles: styles
                                },
                                Settings: copy
                            };
                            this.cdRef.detectChanges();
                        } else {
                            this.ChartData = {
                                Data: source,
                                Settings: copy
                            };
                            this.cdRef.detectChanges();
                        }
                    });
                    return;
                }
                chartData = {
                    Data: source,
                    Settings: copy
                };
            }
        }
        this.ChartData = chartData;
        this.cdRef.detectChanges();
    }
}
